<script setup>
  import { ref, inject, onBeforeMount, watch, computed, onMounted, onUnmounted } from 'vue'
  import AssessmentQuestion from '../AssessmentQuestion.vue'
  import PlanQuestionnaire from '../PlanQuestionnaire/PlanQuestionnaire.vue'
  import PlanningTool from '../PlanningTool.vue'
  import BcsCalendar from '../BcsCalendar.vue'
  import Footer from '../FooterSection.vue'
  import BcsNotes from '../Notes/BcsNotes.vue'
  import { useQuestionsStore } from '../../stores/QuestionsStore'
  import { useEmployerStore } from '../../stores/EmployerStore'
  import { useRenewalEmployerStore } from '../../stores/RenewalEmployerStore'
  import { storeToRefs } from 'pinia'
  import { useNotesStore } from '../../stores/NotesStore'
  import StatusCard from './StatusCard.vue'
  import { RequestService } from '../../services/RequestService'
  import BcsCalendarAtAGlance from '../BcsCalendarAtAGlance.vue'
  import { isToday } from '../../utils'
  import { get } from 'lodash'
  import PlanYearDropdown from '@/shared/components/common/PlanYearDropdown.vue'
  import { PLAN_YEAR_EVENTS } from '@/shared/utils/planYearEvents'
  import { getAccountOwners } from '@/shared/utils/accountOwnersHelpers'
  import * as bootstrap from 'bootstrap'
  import BcsDynamicText from '../BcsDynamicText.vue'
  import IncompleteOverlay from '../IncompleteOverlay.vue'
  import AlertNotification from '../AlertNotification.vue'
  import addIcon from '@/assets/images/add1.svg'

  const emit = defineEmits(['allEmployers'])
  const props = defineProps(['planYearId'])
  const customNavigation = inject('customNavigation')
  const functionsBaseUrl = inject('functionsBaseUrl')
  const isInfoLoaded = ref(false)
  const isRenewalInfoLoaded = ref(false)
  const isNotesLoaded = ref(false)

  const employerStore = useEmployerStore()
  const questionStore = useQuestionsStore()
  const renewalStore = useRenewalEmployerStore()
  const notesStore = useNotesStore()
  const emitter = inject('emitter')

  const { getEmployer, resetAll } = employerStore
  const { employerNotes, userData } = storeToRefs(notesStore)
  const { getEmployerNotes, createEmployerNote, crudNote, convertDate } =
    notesStore
  const { simpleGet, handleErrors } = RequestService()

  const { actualEmployer, actualPlanYear, lastPlanYear } = storeToRefs(employerStore)
  const {
    loadQuestionsAndAnswers,
    isPlanComplete,
    isAssessmentComplete,
    isAssessmentStarted,
    isPlanningStarted,
    resetQuestionStore
  } = questionStore

  const { loadQuestionsAndAnswers: loadRenewalQuestions, isPlanComplete: isRenewalPlanComplete, updateIsRenewalDisabled } = renewalStore
  const { isRenewalDisabled } = storeToRefs(renewalStore)

  const showAssessmentSbMenu = ref(false)
  const noteDescription = ref('')
  const lastLogs = ref(null)
  const isLoadingLastLogs = ref(false)
  const nextReminders = ref([])
  const showRemiderSentModal = ref(false)
  const nextRemindersPlanYear = ref([])
  const calendarDates = ref({
    startDate: null,
    endDate: null,
    endOfPlanYear: null,
    calendarType: null
  })

  const createNote = async () => {
    isNotesLoaded.value = true
    const data = {
      plan_year_id: String(actualPlanYear.value?.id),
      note: noteDescription.value,
      date: new Date().toISOString()
    }

    crudNote()
    await createEmployerNote(data)
    noteDescription.value = ''
    await getEmployerNotes()
    isNotesLoaded.value = false
  }

  // Employer Details card: rendered one owner per line, since up to 3 long emails
  // don't fit on a single line and used to be truncated with an ellipsis.
  const accountOwnersList = computed(() => getAccountOwners(actualEmployer.value))

  const hasRequiredDataCalendar = computed(() => {
    return (
      calendarDates.value.startDate &&
      calendarDates.value.endDate &&
      Array.isArray(nextReminders.value)
    )
  })

const copyCalendar = computed(() => {
  if (!hasRequiredDataCalendar.value) {
    return ''
  }
  const { calendarType, endOfPlanYear } = calendarDates.value
  const remindersCount = nextReminders.value.length
  let startDateConverted = ''
  if (actualPlanYear.value?.plan_start_date && endOfPlanYear) {
    const month = ('0' + (endOfPlanYear.getMonth() + 1)).slice(-2)
    const day = ('0' + endOfPlanYear.getDate()).slice(-2)
    const year = endOfPlanYear.getFullYear()
    startDateConverted = convertDateCalendar(`${month}/${day}/${year}`)
  }
  if (!isInfoLoaded.value) {
    return ''
  }
  if (!isPlanComplete() && isInfoLoaded.value) {
    return `Custom due dates for ${actualEmployer.value.name} will appear once this plan year's Plan Questionnaire is complete, but for now you may view this employer's generic <a href="#" class="calendar-link">Compliance Calendar</a>`;
  }

  const remindersWord = remindersCount === 1 ? 'is' : 'are'
  const remindersText = `<strong>${remindersCount} upcoming due date${remindersCount !== 1 ? 's' : ''}</strong>`

  switch (calendarType) {
    case 2:
      return `There ${remindersWord} ${remindersText} within the first 30 days of the plan year for this employer.`
    case 1:
      return `There ${remindersWord} ${remindersText} within the next 30 days for this employer.`
    case 3:
      return `Your plan year ends on ${startDateConverted}. There ${remindersWord} ${remindersText} within the rest of the plan year for this employer.`
    case 4:
      return `You are viewing a previous plan year. To view this employer’s upcoming due dates, select the current plan year from the Plan Year drop down.`
    default:
      return ''
  }
})

  const getLastLogs = async () => {
    isLoadingLastLogs.value = true
    try {
      const response = await simpleGet(
        'getLastLogsEmployer',
        `${functionsBaseUrl}/loggingGateway/api/v1/logs/lastLogs?planYearId=${props.planYearId}`
      )
      lastLogs.value = response.data.data || {}
    } catch (error) {
      handleErrors('Error fetching last logs:', error)
      lastLogs.value = {}
    } finally {
      isLoadingLastLogs.value = false
    }
  }

  const loadEmployerData = async (planYearId) => {
    isInfoLoaded.value = false
    emitter.emit('toggleLoading', { value: true })
    const employer = await getEmployer(planYearId || props.planYearId)
    if (!employer) {
      emitter.emit('toggleLoading', { value: false })
      customNavigation.value.navigate({
        newPage: customNavigation.value.routes.ListOfEmployers.name
      })
      return
    }
    getEmployerNotes()
    await loadQuestionsAndAnswers()
    // Load questions for the last plan year to check if renewal should be disabled
    loadLastPlanYearQuestions()
    emitter.emit('toggleLoading', { value: false })
    isInfoLoaded.value = true
    getLastLogs()
  }

  const loadLastPlanYearQuestions = async () => {
    if (lastPlanYear.value && lastPlanYear.value.id) {
      await loadRenewalQuestions(lastPlanYear.value.id)
      // Check if plan questionnaire is complete for the last plan year
      updateIsRenewalDisabled(!isRenewalPlanComplete())
      isRenewalInfoLoaded.value = true
    }
  }

  const changePlanYear = async ({ planYearId, omitNavigation = false, goBackToDashboard = false }) => {
    // Reset stores to clear current data
    resetAll()
    resetQuestionStore()
    closeNewPlanYearNotificationDashboard()
    closeNewPlanYearNotificationPlan()
    // Update URL with new plan year ID
    if (!omitNavigation) {
      customNavigation.value.navigate({
        newPage: customNavigation.value.routes.DetailEmployer.name,
        subPage: (!goBackToDashboard && customNavigation.value.currentSubPage) || customNavigation.value.routes.DetailEmployer.children[0].name,
        params: { planYearId: planYearId }
      })
    }
    // Load new data based on selected plan year
    await loadEmployerData(planYearId)
  }

  watch(
    () => customNavigation.value.currentSubPage,
    async (newVal) => {
      if (actualPlanYear.value?.id) {
        isRenewalInfoLoaded.value = false        
        await loadQuestionsAndAnswers()
        loadLastPlanYearQuestions()
        if (
          newVal === customNavigation.value.routes.DetailEmployer.children[2].name ||
          newVal === customNavigation.value.routes.DetailEmployer.children[3].name
        ) {
          showAssessmentSbMenu.value = true
        } else if (newVal === customNavigation.value.routes.DetailEmployer.children[0].name) {
          getLastLogs()
        }
      }
    }
  )

  const subMenuOfAssessment = ['assessmentTool', 'gapReport']

  onBeforeMount(async () => {
    await loadEmployerData()
  })

  watch(() => get(customNavigation.value, 'navigationParams.planYearId'),
    (newVal) => {
      if (actualPlanYear.value?.id && newVal && actualPlanYear.value?.id !== newVal) {
        changePlanYear({ planYearId: newVal, omitNavigation: true })
      }
    }
  )

  onMounted(() => {
    // Initialize Bootstrap tooltips
    setTimeout(() => {
      const tooltipTriggerList = [].slice.call(document.querySelectorAll('[data-bs-toggle="tooltip"]'))
      tooltipTriggerList.forEach(tooltipTriggerEl => {
        new bootstrap.Tooltip(tooltipTriggerEl, {
          trigger: 'hover'
        })
      })
    }, 500)
  })

  const handleDates = (startDate, endDate, endOfPlanYear, calendarType) => {
    calendarDates.value = { startDate, endDate, endOfPlanYear, calendarType }
  }

  const toggleOffCanvas = (id, definitionExpected, date) => {
    emitter.emit('toggleOffCanvas', { id, definitionExpected, date, dashboard: true })
  }

  const handleNextReminders = (nextEventsMonth, nextEventsPlanYear) => {    
    nextReminders.value = nextEventsMonth
    nextRemindersPlanYear.value = nextEventsPlanYear
  }

  const handleReminderSent = (data) => {
    if (data && data.success) {
      showRemiderSentModal.value = true
    }
  }

  const convertDateCalendar = (dateString) => {
    return dateString ?
        new Date(dateString).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric'
        })
      : ''
  }

  const openRenewEmployerModal = () => {
    // Emit event to open the modal with the current employer data
    emitter.emit('open-renew-employer-modal', {
      id: actualEmployer.value.id,
      name: actualEmployer.value.name,
      ein: actualEmployer.value.ein,
      contact_name: actualEmployer.value.contact_name,
      contact_emails: actualEmployer.value.contact_emails,
      plan_start_date: lastPlanYear.value.plan_start_date,
      currentPlanStartDate: actualEmployer.value.plan_start_date, // To show in the modal back buttom
      planYearId: lastPlanYear.value.id, // The latest plan year id available
      accountOwners: getAccountOwners(actualEmployer.value)
    })
  }

  // Add handler and cleanup for CHANGE_PLAN_YEAR event to avoid duplicate subscriptions
  const planYearChangedHandler = ({ planYearId, goBackToDashboard = false }) => {
    changePlanYear({ planYearId, goBackToDashboard })
  }
  // Add handler and cleanup for OPEN_RENEW_EMPLOYER_MODAL event to avoid duplicate subscriptions
  const openRenewModalHandler = () => {
    openRenewEmployerModal()
  }

  emitter.on(PLAN_YEAR_EVENTS.CHANGE_PLAN_YEAR, planYearChangedHandler)
  emitter.on(PLAN_YEAR_EVENTS.OPEN_RENEW_EMPLOYER_MODAL, openRenewModalHandler)

  onUnmounted(() => {
    emitter.off(PLAN_YEAR_EVENTS.CHANGE_PLAN_YEAR, planYearChangedHandler)
    emitter.off(PLAN_YEAR_EVENTS.OPEN_RENEW_EMPLOYER_MODAL, openRenewModalHandler)
  })

  const showNewPlanYearNotificationDashboard = ref(false)
  const showNewPlanYearNotificationPlan = ref(false)

  const closeNewPlanYearNotificationDashboard = () => {
    showNewPlanYearNotificationDashboard.value = false
  }

  const closeNewPlanYearNotificationPlan = () => {
    showNewPlanYearNotificationPlan.value = false
  }

  // Check if the current plan year is a newly created one and show notification
  const checkForNewPlanYearNotification = () => {
    if (actualEmployer.value && actualEmployer.value.planYearId) {
      const storageKeyDashboard = `newPlanYear_dashboard_${actualEmployer.value.planYearId}`
      const storageKeyPlan = `newPlanYear_plan_${actualEmployer.value.planYearId}`
      if (localStorage.getItem(storageKeyDashboard) === 'true') {
        showNewPlanYearNotificationDashboard.value = true
        // Remove from localStorage so it doesn't show again
        localStorage.removeItem(storageKeyDashboard)
      } 
      if (localStorage.getItem(storageKeyPlan) === 'true') {
        showNewPlanYearNotificationPlan.value = true
        // Remove from localStorage so it doesn't show again
        localStorage.removeItem(storageKeyPlan)
      }
    }
  }
  
  // Watch for changes in the actual employer and check for notification
  watch(() => actualPlanYear.value, (newVal) => {
    if (newVal) {
      checkForNewPlanYearNotification()
    }
  }, { immediate: true })

const handleDynamicClick = (event) => {
  event.preventDefault()  
  if (event.target.classList.contains('notes-link')) {
    customNavigation.value.navigate({
      newPage: customNavigation.value.currentPage,
      subPage: customNavigation.value.routes.DetailEmployer.children[6].name
    })
  }
  if (event.target.classList.contains('calendar-link')) {
    customNavigation.value.navigate({
      newPage: customNavigation.value.currentPage,
      subPage: customNavigation.value.routes.DetailEmployer.children[5].name
    })
  }
}

/**
 * Emit event to open Create/Update Employer modal
 */
function emitOpenCreateUpdateEmployerModal() {
  emitter.emit('open-create-update-employer-modal')
}
</script>

<template>
  <div class="container-fluid" v-if="actualEmployer">
    <div class="row">
      <div class="col-2 bg-light px-0 sticky-top text-primary d-flex flex-column">
        <div class="d-inline-flex flex-column w-100">
          <a
            id="backToAllEmployersPage"
            @click="
              customNavigation.navigate({ newPage: customNavigation.routes.ListOfEmployers.name })
            "
            class="px-3 py-3 d-flex align-items-center text-decoration-none border-end hand-cursor">
            <i class="bi bi-chevron-left d-inline float-start"></i>
            <span class="d-inline float-center ms-2 menu-header text-primary">All Employers</span>
          </a>
          <hr class="my-0" />
          <ul class="nav flex-column">
            <li
              id="employerDashboarPrincipalButton"
              @click="
                customNavigation.navigate({
                  newPage: customNavigation.currentPage,
                  subPage: customNavigation.routes.DetailEmployer.children[0].name
                })
              "
              :class="
                (
                  customNavigation.currentSubPage ===
                  customNavigation.routes.DetailEmployer.children[0].name
                ) ?
                  'active'
                : 'border-end'
              "
              class="border-bottom">
              <div class="py-3 px-3 hand-cursor nav-item-sidebar">
                <i class="bi bi-ui-checks-grid"></i>
                <span class="d-inline float-center ms-2 text-16">
                  <med>Employer Dashboard</med>
                </span>
              </div>
            </li>
            <li
              id="intakePrincipalButton"
              @click="
                customNavigation.navigate({
                  newPage: customNavigation.currentPage,
                  subPage: customNavigation.routes.DetailEmployer.children[1].name
                })
              "
              :class="
                (
                  customNavigation.currentSubPage ===
                  customNavigation.routes.DetailEmployer.children[1].name
                ) ?
                  'active'
                : 'border-end'
              "
              class="border-bottom">
              <div id="goToPlanQuestionnaireButton" class="py-3 px-3 hand-cursor nav-item-sidebar">
                <transition name="icon-transition" mode="out-in">
                  <i
                    v-if="isPlanComplete() || !isInfoLoaded"
                    class="bi bi-list-check icon-transition"
                    key="list-check"></i>
                  <i
                    v-else
                    class="bi bi-exclamation-circle-fill text-danger icon-transition"
                    key="exclamation"></i>
                </transition>

                <span class="d-inline float-center ms-2 text-16">
                  <med>Plan Questionnaire</med>
                </span>
              </div>
            </li>

            <!--  -->

            <li
              :class="
                (
                  customNavigation.currentSubPage ===
                    customNavigation.routes.DetailEmployer.children[2].name ||
                  customNavigation.currentSubPage ===
                    customNavigation.routes.DetailEmployer.children[3].name
                ) ?
                  'bg-white'
                : 'bg-light'
              "
              class="border-bottom"
              aria-expanded="false">
              <div
                id="assessmentPrincipalButton"
                @click="showAssessmentSbMenu = !showAssessmentSbMenu"
                :class="showAssessmentSbMenu ? 'border-end border-bottom' : 'border-end'"
                class="py-3 px-3 hand-cursor nav-item-sidebar">
                <i class="bi bi-bar-chart"></i>
                <span class="d-inline float-center ms-2 text-16">
                  <med>Assessment</med>
                </span>
                <i
                  :class="`bi ${showAssessmentSbMenu ? 'bi-caret-up-fill' : 'bi-caret-down-fill'} d-inline float-end`"></i>
              </div>
              <div
                class="collapse"
                :class="{ show: showAssessmentSbMenu }"
                id="home-collapse"
                style="">
                <ul class="btn-toggle-nav list-unstyled fw-normal small internal-nav">
                  <li
                    id="assessmentToolButton"
                    @click="
                      customNavigation.navigate({
                        newPage: customNavigation.currentPage,
                        subPage: customNavigation.routes.DetailEmployer.children[2].name,
                        saveEvent: true
                      })
                    "
                    class="py-3 hand-cursor ps-4 ms-2 internal-nav-item border-end border-bottom"
                    :class="
                      (
                        customNavigation.currentSubPage ===
                        customNavigation.routes.DetailEmployer.children[2].name
                      ) ?
                        'active'
                      : ''
                    ">
                    <span class="d-inline float-center ms-2 text-14">
                      <med>BCS Pro Assessment Tool</med>
                    </span>
                  </li>
                  <li
                    id="gapReportButton"
                    @click="
                      customNavigation.navigate({
                        newPage: customNavigation.currentPage,
                        subPage: customNavigation.routes.DetailEmployer.children[3].name
                      })
                    "
                    class="py-3 hand-cursor ps-4 ms-2 internal-nav-item border-end"
                    :class="
                      (
                        customNavigation.currentSubPage ===
                        customNavigation.routes.DetailEmployer.children[3].name
                      ) ?
                        'active'
                      : ''
                    ">
                    <span class="d-inline float-center ms-2 text-14">
                      <med>BCS Pro Gap Report</med>
                    </span>
                  </li>
                </ul>
              </div>
            </li>

            <li
              id="planningGuidePrincipalButton"
              @click="
                customNavigation.navigate({
                  newPage: customNavigation.currentPage,
                  subPage: customNavigation.routes.DetailEmployer.children[4].name,
                  saveEvent: true
                })
              "
              :class="
                (
                  customNavigation.currentSubPage ===
                  customNavigation.routes.DetailEmployer.children[4].name
                ) ?
                  'active'
                : 'border-end'
              "
              class="border-bottom">
              <div class="py-3 px-3 hand-cursor nav-item-sidebar">
                <i class="bi bi-flag"></i>
                <span class="d-inline float-center ms-2 text-16">
                  <med>Planning Guide</med>
                </span>
              </div>
            </li>
            <li
              id="complianceCalendarPrincipalButton"
              @click="
                customNavigation.navigate({
                  newPage: customNavigation.currentPage,
                  subPage: customNavigation.routes.DetailEmployer.children[5].name,
                  saveEvent: true
                })
              "
              :class="
                (
                  customNavigation.currentSubPage ===
                  customNavigation.routes.DetailEmployer.children[5].name
                ) ?
                  'active'
                : 'border-end'
              "
              class="border-bottom">
              <div class="py-3 px-3 hand-cursor nav-item-sidebar border-end">
                <i class="bi bi-calendar3"></i>
                <span class="d-inline float-center ms-2 text-16">
                  <med>Calendar</med>
                </span>
              </div>
            </li>
            <li
              class="border-bottom"
              id="notesPrincipalButton"
              :class="
                (
                  customNavigation.currentSubPage ===
                  customNavigation.routes.DetailEmployer.children[6].name
                ) ?
                  'active'
                : 'border-end'
              "
              @click="
                customNavigation.navigate({
                  newPage: customNavigation.currentPage,
                  subPage: customNavigation.routes.DetailEmployer.children[6].name
                })
              ">
              <div class="py-3 px-3 hand-cursor nav-item-sidebar border-end">
                <i class="bi bi-pencil"></i>
                <span class="d-inline float-center ms-2 text-16">
                  <med>Notes</med>
                </span>
              </div>
            </li>
          </ul>
        </div>
        <div class="h-100 border-end"></div>
      </div>

      <div
        v-if="
          customNavigation.currentSubPage ===
          customNavigation.routes.DetailEmployer.children[0].name
        "
        class="col-10 px-0 scroll-style">
        <div class="row d-flex justify-content-center px-4">
          <div class="col-12 text-left mt-4.5 mb-4">
            <AlertNotification
              :showCloseButton="true"
              typeAlert="info"
              :showAlert="showNewPlanYearNotificationDashboard"
              @update:showAlert="closeNewPlanYearNotificationDashboard"
              class="mb-4 text-14">
              <strong>
                <BcsDynamicText text="empDashboardNewPlanYearAlertText"/>
              </strong>
            </AlertNotification>

            <div class="d-flex justify-content-between">
              <div class="d-flex align-items-center">              
                <h1 id="employerNameDashboard" class="d-inline-block align-middle me-3 text-28">
                  <med>{{ actualEmployer.name }}</med>
                </h1>
                <PlanYearDropdown />
              </div>
  
              <div class="d-flex align-items-center">
                <button
                  v-if="!isPlanComplete() && !isAssessmentComplete() && isInfoLoaded"
                  @click="
                    customNavigation.navigate({
                      newPage: customNavigation.currentPage,
                      subPage: customNavigation.routes.DetailEmployer.children[1].name
                    })
                  "
                  class="btn complete-plan btn-danger ms-1 me-2 text-nowrap">
                  <i class="bi bi-list-check"></i>
                  &nbsp; Complete Plan Questionnaire
                </button>
                <button v-if="!isRenewalDisabled && isRenewalInfoLoaded" type="button" id="renewThisEmployerButton"
                  class="btn btn-outline-primary d-flex align-items-center ms-2" 
                  @click="openRenewEmployerModal"
                  data-bs-toggle="tooltip"
                  data-bs-placement="top"
                  title="Create a new plan year for this employer.">
                  <i class="bi bi-calendar-plus-fill me-2"></i> <span class="text-16 text-nowrap">Renew This Employer</span>
                </button>
              </div>
            </div>
          </div>
        </div>
        <div class="row d-flex justify-content-center px-4">
          <div class="col-md-5">
            <div class="card h-100">
              <div class="card-header bg-light px-4">
                <h5 class="card-title text-left d-inline float-start header-22 mb-0">
                  <strong>Employer Details</strong>
                </h5>
                <a
                  id="editEmployerButton"
                  class="d-inline float-end hand-cursor text-decoration-none text-14 mt-2"
                  @click.prevent="emitOpenCreateUpdateEmployerModal">
                  <strong>Edit</strong>
                </a>
              </div>
              <div class="card-body px-4 rounded-2">
                <div class="row d-flex justify-content-center">
                  <div class="col-12 pt-2 mt-1">
                    <div class="row">
                      <div class="col-sm-5">
                        <p class="mb-2 text-truncate text-14">
                          <strong>Latest Activity</strong>
                        </p>
                      </div>
                      <div class="col-sm-7">
                        <p class="mb-0 text-truncate text-14 text-primary">
                          <strong>{{ get(lastLogs, 'lastGeneral.description') || 'N/a' }}</strong>
                        </p>
                      </div>
                    </div>
                    <div class="row">
                      <div class="col-sm-5">
                        <p class="mb-2 text-truncate text-14">
                          <strong>Employer Name</strong>
                        </p>
                      </div>
                      <div class="col-sm-7">
                        <p class="mb-0 text-truncate text-14">
                          {{ actualEmployer.name }}
                        </p>
                      </div>
                    </div>
                    <div class="row">
                      <div class="col-sm-5">
                        <p class="mb-2 text-truncate text-14">
                          <strong>EIN</strong>
                        </p>
                      </div>
                      <div class="col-sm-7">
                        <p class="mb-0 text-truncate text-14">
                          {{ actualEmployer.ein }}
                        </p>
                      </div>
                    </div>
                    <div class="row">
                      <div class="col-sm-5">
                        <p class="mb-2 text-truncate text-14">
                          <strong>Plan Year Start</strong>
                        </p>
                      </div>
                      <div class="col-sm-7">
                        <p class="mb-0 text-truncate text-14">
                          {{ actualPlanYear?.plan_start_date }}
                        </p>
                      </div>
                    </div>
                    <div class="row">
                      <div class="col-sm-5">
                        <p class="mb-2 text-truncate text-14">
                          <strong>Primary Contact</strong>
                        </p>
                      </div>
                      <div class="col-sm-7">
                        <p class="mb-0 text-truncate add-teamates-link text-14">
                          {{ actualEmployer.contact_name }}
                        </p>
                      </div>
                    </div>
                    <div class="row">
                      <div class="col-sm-5">
                        <p class="mb-2 text-truncate text-14">
                          <strong>Employer Emails</strong>
                        </p>
                      </div>
                      <div class="col-sm-7">
                        <p class="mb-0 text-truncate add-teamates-link text-14 text-primary">
                          {{ actualEmployer.contact_emails }}
                        </p>
                      </div>
                    </div>
                    <div class="row">
                      <div class="col-sm-5">
                        <p class="mb-2 text-14">
                          <strong>Account Owner(s)</strong>
                        </p>
                      </div>
                      <div class="col-sm-7" id="employerAccountOwners">
                        <p v-if="accountOwnersList.length === 0" class="mb-0 text-14">N/a</p>
                        <p
                          v-for="owner in accountOwnersList"
                          :key="owner"
                          class="mb-0 text-14 text-break">
                          {{ owner }}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="col-md-7">
            <div class="card h-100">
              <div class="card-header bg-light px-4">
                <h5 class="card-title text-left d-inline float-start header-22 mb-0">
                  <strong id="calendarAtAGlanceTitleD">Calendar At-a-Glance</strong>
                </h5>
                <a
                  class="mt-2 d-inline float-end hand-cursor text-decoration-none text-14"
                  @click="
                    customNavigation.navigate({
                      newPage: customNavigation.currentPage,
                      subPage: customNavigation.routes.DetailEmployer.children[5].name,
                      saveEvent: true,
                      actualEmployer
                    })
                  ">
                  <strong id="goToFullComplianceCalendarButton">
                    Go to Full Compliance Calendar
                    <i class="bi bi-chevron-right"></i>
                  </strong>
                </a>
              </div>
              <div class="rounded-2 py-0">
                <div class="d-flex justify-content-center card-container">
                  <div class="col-md-6 calendar-events">
                    <div id="calendarEventsContainer" class="calendar-events__container">
                      <p id="calendarSummaryText" class="text-14" v-html="copyCalendar" @click="handleDynamicClick"></p>
                      <ul v-if="isPlanComplete() && isInfoLoaded" class="nav flex-column mb-auto text-14 events-list">
                        <li
                          @click="toggleOffCanvas(reminder.body, reminder.name, reminder.date)"
                          :key="i"
                          v-for="(reminder, i) in nextReminders"
                          class="nav-item event-item mb-2 mt-1 py-2 bg-warning-subtle hand-cursor"
                        >
                          <div class="event-content">
                            <p class="text-primary mb-0 text-truncate event-name">
                              <strong>{{ reminder.name }}</strong>
                            </p>
                          </div>
                          <div class="event-date">
                            <p class="text-primary mb-0 text-truncate">
                              <strong>{{ reminder.formatDate }}</strong>
                            </p>
                          </div>
                        </li>
                      </ul>
                    </div>
                  </div>
                  <div class="col-md-6">
                    <BcsCalendarAtAGlance
                      @dateRange="({ startDate, endDate, endOfPlanYear, calendarType }) => handleDates(startDate, endDate, endOfPlanYear, calendarType)"
                      @nextReminders="({nextEventsMonth, nextEventsPlanYear}) => handleNextReminders(nextEventsMonth, nextEventsPlanYear)"
                      @reminderSent="handleReminderSent"
                      v-if="actualPlanYear" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="row d-flex justify-content-between mt-4 px-4 center-modules">
          <div class="col-md-5">
            <StatusCard
              :isAssessmentComplete="isAssessmentComplete()"
              :isAssessmentStarted="isAssessmentStarted()"
              :isPlanningStarted="isPlanningStarted()"
              :isPlanComplete="isPlanComplete()"
              :isLoadingLastLogs="isLoadingLastLogs"
              :lastLogs="lastLogs"
              name="gapReport" />
          </div>

          <div class="col-md-7">
            <div class="row d-flex justify-content-between h-100">
              <div class="col-md-6">
                <StatusCard
                  :isAssessmentComplete="isAssessmentComplete()"
                  :isAssessmentStarted="isAssessmentStarted()"
                  :isPlanningStarted="isPlanningStarted()"
                  :isPlanComplete="isPlanComplete()"
                  :isLoadingLastLogs="isLoadingLastLogs"
                  :lastLogs="lastLogs"
                  name="assessment" />
              </div>
              <div class="col-md-6">
                <StatusCard
                  :isAssessmentComplete="isAssessmentComplete()"
                  :isAssessmentStarted="isAssessmentStarted()"
                  :isPlanningStarted="isPlanningStarted()"
                  :isPlanComplete="isPlanComplete()"
                  :isLoadingLastLogs="isLoadingLastLogs"
                  :lastLogs="lastLogs"
                  name="planningGuide" />
              </div>
            </div>
          </div>
        </div>
        <div class="col-md-12 general-notes px-4">
          <div class="card h-100">
            <div class="card-header notes-header d-flex justify-content-between align-items-center ">
              <h5
                class="card-title text-left d-flex align-items-center  general-notes__title header-22 mb-0">
                <strong>General Notes</strong>

                <div id="notes-count" class="notes-count-container" style="margin-left: 15px;">
                  <span
                  :style="{
                    color: employerNotes.length > 0 ? 'var(--bs-primary)' : '#000'
                  }"
                  class="text-14">
                  {{ employerNotes.length }} 
                </span>
                </div>
             
              </h5>

              <div
                    class="view-notes-page text-14 text-primary"
                    @click="
                      customNavigation.navigate({
                        newPage: customNavigation.currentPage,
                        subPage: customNavigation.routes.DetailEmployer.children[6].name
                      })
                    ">
                  <strong id="goToNotesPageButton">Go to Notes Page</strong> 
                    <i class="bi bi-chevron-right"></i>
                  </div>
            </div>
            <div class="rounded-2">
              <div class="row d-flex justify-content-center">
                <div class="col-md-6 general-notes__left border-end border-end-1">
                  <div id="generalNotesSection" class="general-notes__left-notes text-14">
                    <div v-if="employerNotes.length > 0">
                      <div v-for="note in employerNotes" :key="note.id" class="note-item">
                        <div class="d-flex justify-content-between">
                          <div>
                            <strong class="employer">{{ userData?.fullname }}</strong>
                          </div>
                          <div class="last-edit">
                            Last Edit:
                            {{
                              isToday(note.date) ? 'Today' : convertDate(note.date)
                            }}
                          </div>
                        </div>

                        <div class="note">
                          {{ note.note }}
                        </div>
                      </div>
                    </div>

                    <div
                      id="emptyNotesText"
                      v-else
                      class="empty-notes d-flex flex-column justify-content-center align-items-center">
                      <img :src="addIcon" alt="add-1" />
                      <div @click="handleDynamicClick" class="empty-notes-txt">
                        <BcsDynamicText text="employerNotesEmptyStateText" />
                      </div>
                    </div>
                  </div>

            
                </div>
                <div class="col-md-6 text-14 general-notes__right">
                  <div class="info-message">
                    Note
                  </div>

                  <textarea
                    id="noteDescription"
                    class="form-control"
                    rows="5"
                    v-model="noteDescription"
                    placeholder="Enter employer notes here."></textarea>

                  <span
                    :class="{ 'error-hint': noteDescription.length > 1300 }"
                    class="remaining-characters text-12 d-flex justify-content-end">
                    {{ noteDescription ? 1300 - noteDescription.length : 1300 }} characters
                    remaining
                  </span>
                  <div class="action-buttons d-flex justify-content-end">
                    <button
                      id="resetNoteButton"
                      :disabled="noteDescription.length === 0"
                      @click="noteDescription = ''"
                      class="btn btn-info text-14 cancel-btn fw-medium">
                      Reset
                    </button>
                    <button
                      id="createNoteButtonEmplDashboard"
                      :disabled="noteDescription.length > 1300 || noteDescription.length === 0 || isNotesLoaded"
                      @click="createNote"
                      class="btn btn-primary text-14 save-btn">
                      Save Note
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="row d-flex mt-3 3">
          <div class="col-12 p-0">
            <Footer class="px-5 mx-n3"></Footer>
          </div>
        </div>
      </div>

      <PlanQuestionnaire
        :showNewPlanYearNotification="showNewPlanYearNotificationPlan"
        @closeNewPlanYearNotification="closeNewPlanYearNotificationPlan"
        v-if="
          customNavigation.currentSubPage ===
          customNavigation.routes.DetailEmployer.children[1].name
        "
        ></PlanQuestionnaire>

      <AssessmentQuestion
        v-if="subMenuOfAssessment.includes(customNavigation.currentSubPage)"></AssessmentQuestion>
      <PlanningTool
        v-if="
          customNavigation.currentSubPage ===
          customNavigation.routes.DetailEmployer.children[4].name
        "
        ></PlanningTool>
      <BcsCalendar
        v-if="
          customNavigation.currentSubPage ===
          customNavigation.routes.DetailEmployer.children[5].name
        "
        ></BcsCalendar>

      <BcsNotes
        v-if="
          customNavigation.currentSubPage ===
          customNavigation.routes.DetailEmployer.children[6].name
        "/>

      <IncompleteOverlay
        v-if="
          !isPlanComplete() &&
          isInfoLoaded &&
          customNavigation.currentSubPage ===
            customNavigation.routes.DetailEmployer.children[2].name
        "
        title="Plan Questionnaire Incomplete"
        dynamicTextKey="planQuestionnaireIncompleteText"
        buttonLabel="Complete Plan Questionnaire"
        @navigate="
          customNavigation.navigate({
            newPage: customNavigation.currentPage,
            subPage: customNavigation.routes.DetailEmployer.children[1].name
          })
        "
      />

      <!-- <div
        v-if="
          !isPlanComplete() &&
          !isAssessmentComplete() && isInfoLoaded &&
          customNavigation.currentSubPage === customNavigation.routes.DetailEmployer.children[3].name
        "
        class="col-10 overlay-white-left">
        <div class="card mt-5 d-flex mx-auto">
          <div
            class="card-body text-center d-flex flex-column justify-content-center align-items-center">
            <h5 class="card-title header-22">
              <i class="bi bi-exclamation-circle-fill text-danger"></i>
              &nbsp;
              <strong>Plan Questionnaire And Assessment Tool Incomplete</strong>
            </h5>
            <p class="card-text text-14 text-gray-700">
              It looks like the BCS Pro Plan Questionnaire and Assessment Tool have not been
              completed. Please first fill out the Plan Questionnaire, followed by the assessment,
              to view this group’s BCS Pro Gap Report.
            </p>
            <button
              @click="
                customNavigation.navigate({
                  newPage: customNavigation.currentPage,
                  subPage: customNavigation.routes.DetailEmployer.children[1].name
                })
              "
              class="btn btn-primary">
              <i class="bi bi-pencil"></i>
              &nbsp; Complete Plan Questionnaire
            </button>
          </div>
        </div>
      </div> -->

      <IncompleteOverlay
        v-if="
          !isPlanComplete() &&
          isInfoLoaded &&
          customNavigation.currentSubPage ===
            customNavigation.routes.DetailEmployer.children[3].name
        "
        title="Plan Questionnaire Incomplete"
        dynamicTextKey="planQuestionnaireIncompleteTextForGapReport"
        buttonLabel="Complete Plan Questionnaire"
        :showSampleReportLink="true"
        sampleReportPath="sample-resources/gap-report-sample.pdf"
        @navigate="
          customNavigation.navigate({
            newPage: customNavigation.currentPage,
            subPage: customNavigation.routes.DetailEmployer.children[1].name
          })
        "
      />

      <IncompleteOverlay
        v-if="
          isPlanComplete() &&
          !isAssessmentComplete() &&
          isInfoLoaded &&
          customNavigation.currentSubPage ===
            customNavigation.routes.DetailEmployer.children[3].name
        "
        title="Assessment Tool Incomplete"
        dynamicTextKey="assessmentToolIncompleteText"
        buttonLabel="Complete Assessment Tool"
        @navigate="
          customNavigation.navigate({
            newPage: customNavigation.currentPage,
            subPage: customNavigation.routes.DetailEmployer.children[2].name,
            saveEvent: true
          })
        "
      />

      <IncompleteOverlay
        v-if="
          !isPlanComplete() &&
          isInfoLoaded &&
          customNavigation.currentSubPage ===
            customNavigation.routes.DetailEmployer.children[4].name
        "
        title="Plan Questionnaire Incomplete"
        staticText="It looks like the BCS Pro Plan Questionnaire has not been completed. We'll need that information to provide you the right planning guide for this group."
        buttonLabel="Complete Plan Questionnaire"
        @navigate="
          customNavigation.navigate({
            newPage: customNavigation.currentPage,
            subPage: customNavigation.routes.DetailEmployer.children[1].name
          })
        "
      />
    </div>
  </div>
 
 
  <!-- SUCCESS MODAL -->
  <div v-if="showRemiderSentModal" class="col-10 overlay-white-left modal-reminder-container">
    <div class="card mt-5 d-flex mx-auto">
      <div
        class="card-body modal-reminder-sent text-center d-flex flex-column justify-content-center align-items-center">
        <h5 class="card-title header-22">
          <i class="bi bi-check-circle icon-created"></i>
          <div class="modal-body-notification-title">
            <b>Reminder Created!</b>
          </div>
        </h5>
        <p class="card-text text-16 text-gray-700">Your selected reminders were sent!</p>
        <button @click="showRemiderSentModal = false" class="btn btn-primary">
          <i class="bi bi-x-circle"></i>
          &nbsp; Close
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .internal-nav .internal-nav-item.active {
    background-color: var(--bs-white);
    border-right: 0 !important;
    color: var(--bs-gray-700);
  }

  .internal-nav .internal-nav-item:not(.active):hover {
    background-color: var(--bs-primary) !important;
    color: var(--bs-white);
  }

  .nav :not(.active) .nav-item-sidebar:hover {
    background-color: var(--bs-primary) !important;
    color: var(--bs-white);
  }

  .nav .active .nav-item-sidebar {
    background-color: var(--bs-white);
    border-right: 0 !important;
  }
  .nav .nav-item.bg-warning-subtle:hover {
    background-color: #ffe69c !important;
    p {
      strong {
        color: #8f38f2 !important;
      }
    }
  }
  .menu-header {
    font-size: 1rem;
    font-style: normal;
    font-weight: 500;
    line-height: 150%;
  }

  .icon-bg {
    width: 50px;
    height: 50px;
    border-radius: 50%;
    background-color: white;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .view-notes-page {
    cursor: pointer;

    i {
      padding-left: 5px;
    }
  }

  .notes-count-container{
    width: min-content;
    height: auto;
    min-width: 25px;
    min-height: 25px;
  }

  .icon {
    width: 29px;
    height: 29px;
  }

  .header-text {
    color: var(--bs-gray-600);
  }
  

  .card-body:not(.modal-reminder-sent) {
    height: 40%;
    max-height: 40%;
  }

  .center-modules {
    min-height: 300px;
  }

  .complete-plan {
    height: 35px;
    color: #fff;
    display: flex;
    align-items: center;
    // background-color: #dc3545;
  }

  .card-footer {
    background: transparent;
    border-top: 0px;
  }

  .scroll-style {
    overflow-y: scroll;
    height: 95vh;
    overflow-x: hidden;
  }

  .container-fluid{
    overflow-x: hidden;
  }

  .icon-transition-enter-active,
  .icon-transition-leave-active {
    transition: opacity 0.01s;
  }
  .icon-transition-enter,
  .icon-transition-leave-to {
    opacity: 0;
  }

  .general-notes {
    margin-top: 28px;

    &__title {
      span {
        font-weight: 700 !important;
        background-color: #fff;
        width: 100%;
        height: 100%;
        display: flex;
        justify-content: center;
        align-items: center;
        border-radius: 128392183092830923px;
        padding: 4.2px 7.8px;
      }
    }

    &__left {
      padding: 0 !important;

      &-notes {
        padding: 21px 30px 0 30px;

        height: 207px;
        overflow-y: scroll;

        .last-edit {
          color: var(--bs-gray-600);
          font-weight: 300;
        }

        .note {
          margin-top: 5px;
          color: var(--bs-gray-700);
          margin-bottom: 20px;
        }

        .employer {
          color: var(--bs-gray-700);
        }
      }
    }

    &__right {
      padding-right: 40px;
      padding-left: 28px;

      .info-message {
        color: var(--bs-gray-700);
        margin-top: 20px;
        margin-bottom: 8px;
      }
    }
  }

  textarea {
    border-color: var(--bs-gray-600);
  }

  textarea::placeholder {
    color: var(--bs-gray-600);
    font-weight: 300;
  }

  .remaining-characters {
    margin-top: 2px;
    margin-bottom: 16px;
    color: var(--bs-gray-600);
  }

  .action-buttons {
    gap: 13px;
    padding-bottom: 21px;
  }

  .empty-notes {
    height: 100%;
    gap: 8px;

    &-txt {
      width: 97%;
      text-align: center;
      color: var(--bs-gray-700);
    }
  }

  .notes-header{
    background-color: var(--bs-gray-100)!important;
  }

  .calendar-events {
    border-right: 1px solid var(--bs-gray-300);

    &__container {
      height: 100%;
      padding: 28px 35px;
      overflow-y: auto;
    }
  }

  .calendar-title {
    i {
      padding-right: 14px;
    }
  }

  .event-item {
    border-radius: 3428932489px;
    width: 100%;
    display: grid;
    grid-template-columns: 1fr auto;
    gap: 0;
    align-items: center;
    padding: 0 20px;

    .event-content {
      overflow: hidden;
      padding-right: 15px;
    }

    .event-name {
      width: 100%;
    }

    .event-date {
      white-space: nowrap;
      min-width: 44px;
      justify-self: end;
    }
  }

  .card-container {
    max-height: 240px;
    height: 240px;
  }
  .overlay-white-left {
    z-index: 99999;
  }

  .modal-reminder-sent {
    .icon-created {
      font-size: 50px;
    }

    .card-title {
      color: #198754;
    }
  }
</style>
